//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer do Portal LiveModa (portal-b1-moda-front,
// page-specific-components/smartflow/SmartFlowImporterForm.js — Tailwind + primitivos próprios)
// para a plataforma SmartFlow, reescrito em reactstrap. A CONFIGURAÇÃO (2 pastas do Drive, Grupo
// do PN, sequência do CardCode) ficou nos dados do PROJETO (modal "Alterando Projeto",
// components/SmartFlow/SmartFlowImporterConfig.js) — por isso não há mais a aba "Configuração"
// aqui: só a barra "Listar arquivos" + 3 abas (Notas identificadas / Validação Receita /
// Validação SAP).
//
// A gravação real no SAP usa a Service Layer do PROJETO (project.serviceLayerUrl) — o login
// (/Login) é feito pela view antes de montar este componente (SmartFlowImporter.js), igual ao
// ProjectConsole. A lógica pura (montarParceiroSap, validarParceiroSap, comparação com a Receita)
// está em components/SmartFlow/importerLogic.js.
//======
import React, { useEffect, useMemo, useState } from "react"
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Input,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap"
import classnames from "classnames"
import { utils, writeFile } from "xlsx"

import {
  listDriveFiles,
  validarReceitaCnpj,
  salvarParceirosStaging,
  buscarParceirosJaImportadosPorCnpj,
  buscarParceirosGravadosNoSapPorCnpj,
  incrementarProximoNumeroParceiro,
  lerSequenciaDoProjeto,
} from "services/smartflow/importerApi"
import {
  gravarParceiroNoSap,
  buscarCardCodesPorDocumentos,
} from "services/smartflow/smartflowSapWrite"
import {
  formatDataEmissao,
  formatCnpjCpf,
  formatValor,
  formatEndereco,
  compararComReceita,
  montarParceiroSap,
  validarParceiroSap,
} from "components/SmartFlow/importerLogic"

function TipoBadge({ tipo }) {
  return (
    <Badge color={tipo === "cliente" ? "info" : "success"} pill>
      {tipo === "cliente" ? "Cliente" : "Fornecedor"}
    </Badge>
  )
}

export default function SmartFlowImporterForm({ project, userEmail, onNotify }) {
  const projectId = project?._id
  const serviceLayerUrl = project?.serviceLayerUrl || ""
  const importerCfg = project?.smartflowImporter || {}

  const notify = (msg, type = "info") => {
    if (onNotify) onNotify(msg, type)
    else console.log(`[SmartFlow][${type}] ${msg}`)
  }

  const pastasConectadas = ["fornecedor", "cliente"].filter(
    (t) => importerCfg?.[t]?.driveFolderId
  )

  const [openTab, setOpenTab] = useState("notas")

  const [notas, setNotas] = useState(null)
  const [naoIdentificados, setNaoIdentificados] = useState([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState("")
  const [filtroNotas, setFiltroNotas] = useState("")
  const [parceirosJaImportados, setParceirosJaImportados] = useState({})
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const [receitaRows, setReceitaRows] = useState(null)
  const [receitaLoading, setReceitaLoading] = useState(false)
  const [selectedReceitaIds, setSelectedReceitaIds] = useState(() => new Set())

  const [importRows, setImportRows] = useState(null)
  const [selectedImportIds, setSelectedImportIds] = useState(() => new Set())
  const [gravandoStaging, setGravandoStaging] = useState(false)
  const [resultadosGravacao, setResultadosGravacao] = useState({})
  //====== WR data 04/09/2026
  // Motivo da alteração: pedido do usuário ("está ultra lento") — "Gravar SAP" faz várias
  // chamadas SEQUENCIAIS por parceiro (checar existência, resolver município, gravar,
  // Identif.fiscais, marcar staging) contra uma Service Layer remota (~1,2s por chamada medido) —
  // não é um travamento, é o tempo real da operação (mesma sequência do LiveModa). Sem indicador
  // de progresso, a tela ficava muda por 20-30s parecendo quebrada. Contador simples "X de Y".
  // ======
  const [progressoGravacao, setProgressoGravacao] = useState(null)

  //====== Listar arquivos — 1x por pasta conectada (paralelo), junta tudo com `tipo` por nota.
  const handleListar = async () => {
    if (pastasConectadas.length === 0) {
      notify(
        "Nenhuma pasta do Drive configurada. Configure em Alterando Projeto > SmartFlow Importer.",
        "warning"
      )
      return
    }

    setOpenTab("notas")
    setListLoading(true)
    setListError("")

    try {
      const resultados = await Promise.allSettled(
        pastasConectadas.map((tipo) =>
          listDriveFiles(importerCfg[tipo].driveFolderId, tipo).then((result) => ({ tipo, result }))
        )
      )

      let notasEncontradas = []
      let naoIdentEncontradas = []
      const mensagensErro = []

      for (const item of resultados) {
        if (item.status === "fulfilled") {
          const { tipo, result } = item.value
          notasEncontradas = notasEncontradas.concat(
            (result.notas || []).map((n) => ({ ...n, tipo }))
          )
          naoIdentEncontradas = naoIdentEncontradas.concat(result.naoIdentificados || [])
        } else {
          mensagensErro.push(item.reason?.message || "falha ao listar uma das pastas")
        }
      }

      setNotas(notasEncontradas)
      setNaoIdentificados(naoIdentEncontradas)
      setSelectedIds(new Set())
      setReceitaRows(null)

      if (mensagensErro.length > 0) {
        const message = `Falha ao listar: ${mensagensErro.join("; ")}`
        setListError(message)
        notify(message, "danger")
      }

      try {
        const mapa = await buscarParceirosJaImportadosPorCnpj(
          projectId,
          notasEncontradas.map((n) => n.cnpjCpf)
        )
        setParceirosJaImportados(mapa)
      } catch (error) {
        console.error("[SmartFlow] Falha ao checar parceiros já importados:", error)
        setParceirosJaImportados({})
      }
    } finally {
      setListLoading(false)
    }
  }

  const toggleSelecionada = (fileId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const notasFiltradas = useMemo(() => {
    const termo = filtroNotas.trim().toLowerCase()
    if (!termo) return notas || []
    return (notas || []).filter((nota) =>
      [nota.numeroNF, nota.cnpjCpf, nota.idEstrangeiro, nota.razaoSocial, nota.nomeFantasia, nota.fileName]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(termo))
    )
  }, [notas, filtroNotas])

  const todasSelecionadas =
    notasFiltradas.length > 0 && notasFiltradas.every((n) => selectedIds.has(n.fileId))

  const toggleSelecionarTodas = () => {
    setSelectedIds(() => (todasSelecionadas ? new Set() : new Set(notasFiltradas.map((n) => n.fileId))))
  }

  //====== Validar Receita — sequencial (BrasilAPI é pública, sem SLA).
  const handleValidarReceita = async () => {
    const selecionadas = (notas || []).filter((n) => selectedIds.has(n.fileId))
    if (selecionadas.length === 0) {
      notify("Marque pelo menos uma nota antes de validar na Receita", "warning")
      return
    }

    setOpenTab("receita")
    setReceitaLoading(true)

    const rows = []
    for (const nota of selecionadas) {
      const digits = String(nota.cnpjCpf || "").replace(/\D/g, "")
      if (digits.length !== 14) {
        const motivoNaoAplicavel =
          digits.length === 11
            ? "CPF — validação na Receita não se aplica"
            : "Parceiro estrangeiro — validação na Receita não se aplica"
        rows.push({ ...nota, receita: null, receitaErro: null, receitaNaoAplicavel: true, motivoNaoAplicavel })
        continue
      }
      try {
        const receita = await validarReceitaCnpj(digits)
        rows.push({ ...nota, receita, receitaErro: null, receitaNaoAplicavel: false })
      } catch (error) {
        rows.push({
          ...nota,
          receita: null,
          receitaErro: error?.message || "Falha ao consultar esse CNPJ na Receita.",
          receitaNaoAplicavel: false,
        })
      }
    }

    setReceitaRows(rows)
    setReceitaLoading(false)
    setSelectedReceitaIds(new Set())
    setImportRows(null)
    setSelectedImportIds(new Set())
  }

  const toggleSelecionadaReceita = (fileId) => {
    setSelectedReceitaIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const receitaSelecionaveis = (receitaRows || []).filter((r) => !r.receitaErro)
  const todasReceitaSelecionadas =
    receitaSelecionaveis.length > 0 &&
    receitaSelecionaveis.every((r) => selectedReceitaIds.has(r.fileId))

  const toggleSelecionarTodasReceita = () => {
    setSelectedReceitaIds(() =>
      todasReceitaSelecionadas ? new Set() : new Set(receitaSelecionaveis.map((r) => r.fileId))
    )
  }

  //====== Preparar dados — dedupe por CNPJ/idEstrangeiro, sequência do projeto por papel.
  const handlePrepararImportacao = async () => {
    const selecionadas = receitaSelecionaveis.filter((r) => selectedReceitaIds.has(r.fileId))
    if (selecionadas.length === 0) {
      notify("Marque pelo menos uma nota antes de preparar a importação", "warning")
      return
    }

    const sequenciaPorTipo = {
      fornecedor: lerSequenciaDoProjeto(project, "fornecedor"),
      cliente: lerSequenciaDoProjeto(project, "cliente"),
    }

    // Pré-checagem: quais CNPJ/CPF já existem — no espelho (Mongo) E direto no SAP (fonte de
    // verdade). O SAP tem prioridade. Assim as linhas que vão virar ATUALIZAÇÃO já aparecem
    // marcadas (amarelo, com o CardCode real) em vez de o usuário só descobrir no erro do Gravar.
    let gravadosNoSap = {}
    const docs = selecionadas.map((r) => r.cnpjCpf)
    try {
      gravadosNoSap = await buscarParceirosGravadosNoSapPorCnpj(projectId, docs)
    } catch (error) {
      console.error("[SmartFlow] Falha ao checar parceiros já gravados (Mongo):", error)
    }
    if (serviceLayerUrl) {
      try {
        const noSap = await buscarCardCodesPorDocumentos(serviceLayerUrl, docs)
        gravadosNoSap = { ...gravadosNoSap, ...noSap }
      } catch (error) {
        console.error("[SmartFlow] Falha ao checar parceiros já existentes no SAP:", error)
      }
    }

    const vistos = new Set()
    const rows = []
    const contador = {
      fornecedor: (sequenciaPorTipo.fornecedor?.proximoNumero || 1) - 1,
      cliente: (sequenciaPorTipo.cliente?.proximoNumero || 1) - 1,
    }

    for (const row of selecionadas) {
      const digits = String(row.cnpjCpf || "").replace(/\D/g, "")
      const idEstrangeiroTrim = String(row.idEstrangeiro || "").trim()
      const chaveDedup =
        digits || (idEstrangeiroTrim ? `estrangeiro:${idEstrangeiroTrim}` : `arquivo:${row.fileId}`)
      if (vistos.has(chaveDedup)) continue
      vistos.add(chaveDedup)

      const tipo = row.tipo === "cliente" ? "cliente" : "fornecedor"
      const seq = sequenciaPorTipo[tipo]
      const cardCodeExistente = gravadosNoSap[digits]
      let cardCode = cardCodeExistente || null
      const usaSequencia = !cardCodeExistente && !!seq?.usaPrefixo
      if (usaSequencia) {
        contador[tipo] += 1
        cardCode = `${seq.prefixo}${String(contador[tipo]).padStart(seq.qtdCaracteresSequencia, "0")}`
      }

      const grupoCode = importerCfg?.[tipo]?.grupoCode
      rows.push(montarParceiroSap(row, cardCode, !!cardCodeExistente, usaSequencia, tipo, grupoCode))
    }

    setImportRows(rows)
    setSelectedImportIds(new Set(rows.map((r) => r.fileId)))
    setResultadosGravacao({})
    setOpenTab("importacao")

    const duplicados = selecionadas.length - rows.length
    const jaExistem = rows.filter((r) => r.jaGravadoNoSap).length
    const avisos = []
    if (duplicados > 0) {
      avisos.push(
        `${duplicados} nota${duplicados === 1 ? "" : "s"} ignorada${
          duplicados === 1 ? "" : "s"
        } por repetir o mesmo parceiro.`
      )
    }
    if (jaExistem > 0) {
      avisos.push(
        `${jaExistem} parceiro${jaExistem === 1 ? "" : "s"} já existe${
          jaExistem === 1 ? "" : "m"
        } no SAP (linha amarela) — serão ATUALIZADOS, não criados.`
      )
    }
    if (avisos.length) notify(avisos.join(" "), "warning")
  }

  const updateImportField = (fileId, field, value) => {
    setImportRows((prev) => prev.map((r) => (r.fileId === fileId ? { ...r, [field]: value } : r)))
  }

  const validacoesImport = useMemo(() => {
    const contagemCardCode = new Map()
    ;(importRows || []).forEach((row) => {
      const code = (row.CardCode || "").trim()
      if (!code) return
      contagemCardCode.set(code, (contagemCardCode.get(code) || 0) + 1)
    })
    const mapa = new Map()
    ;(importRows || []).forEach((row) => {
      mapa.set(row.fileId, validarParceiroSap(row, contagemCardCode))
    })
    return mapa
  }, [importRows])

  const importSelecionaveis = (importRows || []).filter((r) => validacoesImport.get(r.fileId)?.ok)

  useEffect(() => {
    setSelectedImportIds((prev) => {
      const validos = new Set(importSelecionaveis.map((r) => r.fileId))
      const next = new Set([...prev].filter((fileId) => validos.has(fileId)))
      return next.size === prev.size ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validacoesImport])

  const toggleImportFlag = (fileId) => {
    if (!validacoesImport.get(fileId)?.ok) return
    setSelectedImportIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const todosImportSelecionados =
    importSelecionaveis.length > 0 && importSelecionaveis.every((r) => selectedImportIds.has(r.fileId))

  const toggleSelecionarTodosImport = () => {
    setSelectedImportIds(() =>
      todosImportSelecionados ? new Set() : new Set(importSelecionaveis.map((r) => r.fileId))
    )
  }

  //====== Gravar SAP — staging (backend) + POST/PATCH BusinessPartners (Service Layer do projeto).
  const handleGravarStaging = async () => {
    const selecionados = (importRows || []).filter(
      (r) => selectedImportIds.has(r.fileId) && validacoesImport.get(r.fileId)?.ok
    )
    if (selecionados.length === 0) {
      notify("Marque pelo menos um parceiro válido antes de gravar", "warning")
      return
    }
    if (!serviceLayerUrl) {
      notify("O projeto não tem a URL da Service Layer configurada.", "danger")
      return
    }

    setGravandoStaging(true)
    setProgressoGravacao({ atual: 0, total: selecionados.length, etapa: "Salvando no banco..." })

    const usouSequenciaPorCardCode = new Map(selecionados.map((r) => [r.CardCode, !!r.usouSequencia]))
    const fileIdPorCardCode = new Map(selecionados.map((r) => [r.CardCode, r.fileId]))

    try {
      const salvos = await salvarParceirosStaging(projectId, selecionados, userEmail || null)

      let criados = 0
      let atualizados = 0
      const numerosConsumidosPorTipo = { fornecedor: 0, cliente: 0 }
      const erros = []
      const novosResultados = {}

      let indice = 0
      for (const parceiro of salvos || []) {
        indice += 1
        setProgressoGravacao({
          atual: indice,
          total: salvos.length,
          etapa: `Gravando no SAP: ${parceiro.cardName || parceiro.cardCode}...`,
        })
        const fileId = fileIdPorCardCode.get(parceiro.cardCode)
        try {
          const resultado = await gravarParceiroNoSap(parceiro, userEmail || null, serviceLayerUrl)
          // O CardCode pode ter mudado (CNPJ já existia sob outro CardCode) — reflete na grade.
          if (fileId && resultado.cardCode && resultado.cardCode !== parceiro.cardCode) {
            updateImportField(fileId, "CardCode", resultado.cardCode)
          }
          if (resultado.acaoNoSap === "atualizado") {
            atualizados += 1
          } else {
            criados += 1
            if (usouSequenciaPorCardCode.get(parceiro.cardCode)) {
              const tipo = parceiro.cardType === "cCustomer" ? "cliente" : "fornecedor"
              numerosConsumidosPorTipo[tipo] += 1
            }
          }
          if (fileId) novosResultados[fileId] = { status: "sucesso" }
        } catch (error) {
          const mensagem = error?.message || "falha desconhecida"
          erros.push(`${parceiro.cardCode}: ${mensagem}`)
          if (fileId) novosResultados[fileId] = { status: "erro", mensagem }
        }
      }
      setResultadosGravacao((prev) => ({ ...prev, ...novosResultados }))

      if (numerosConsumidosPorTipo.fornecedor > 0) {
        incrementarProximoNumeroParceiro(projectId, numerosConsumidosPorTipo.fornecedor, "fornecedor")
      }
      if (numerosConsumidosPorTipo.cliente > 0) {
        incrementarProximoNumeroParceiro(projectId, numerosConsumidosPorTipo.cliente, "cliente")
      }

      const sucesso = criados + atualizados
      const resumoSucesso = [
        criados > 0 ? `${criados} criado${criados === 1 ? "" : "s"}` : null,
        atualizados > 0 ? `${atualizados} atualizado${atualizados === 1 ? "" : "s"}` : null,
      ]
        .filter(Boolean)
        .join(", ")

      if (erros.length === 0) {
        notify(`${resumoSucesso} no SAP.`, "success")
      } else if (sucesso === 0) {
        notify(`Falha ao gravar no SAP — ${erros.join(" | ")}`, "danger")
      } else {
        notify(`${resumoSucesso} no SAP. Falhas: ${erros.join(" | ")}`, "warning")
      }
    } catch (error) {
      notify(error?.message || "Falha ao gravar os parceiros.", "danger")
    } finally {
      setGravandoStaging(false)
      setProgressoGravacao(null)
    }
  }

  const handleExportarExcel = () => {
    if (!importRows?.length) return
    const linhas = importRows.map((row) => {
      const validacao = validacoesImport.get(row.fileId) || { ok: true, erros: [] }
      return {
        "Nota de origem": `${row.numeroNF || "—"} — ${row.fileName}`,
        Tipo: row.tipo === "cliente" ? "Cliente" : "Fornecedor",
        CardCode: row.CardCode,
        CardType: row.CardType === "cCustomer" ? "Cliente" : "Fornecedor",
        "CardName (Razão social)": row.CardName,
        "CardForeignName (Nome fantasia)": row.CardForeignName,
        "FederalTaxID (CNPJ)": row.FederalTaxID,
        "Inscrição Estadual": row.InscricaoEstadual,
        "E-mail": row.Email,
        Telefone: row.Telefone,
        "Tipo de Logradouro": row.TipoLogradouro,
        Logradouro: row.Street,
        "Número": row.StreetNo,
        Complemento: row.Complemento,
        Bairro: row.Block,
        Cidade: row.City,
        UF: row.County,
        CEP: row.ZipCode,
        Valid: row.Valid === "tYES" ? "Ativo" : "Inativo",
        "TaxID Estrangeiro": row.TaxIdEstrangeiro,
        "Status SAP": validacao.ok ? "OK" : `${validacao.erros.length} erro(s)`,
        "Detalhe dos erros": validacao.erros.join(" "),
        Selecionado: selectedImportIds.has(row.fileId) ? "Sim" : "Não",
      }
    })
    const planilha = utils.json_to_sheet(linhas)
    const livro = utils.book_new()
    utils.book_append_sheet(livro, planilha, "Validação SAP")
    writeFile(livro, `smartflow-validacao-sap-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const camposDoc = String(project?.serviceLayerUrl || "").trim()

  return (
    <Card>
      <CardBody>
        {pastasConectadas.length === 0 && (
          <div className="alert alert-warning" role="alert">
            Nenhuma pasta do Google Drive configurada para este projeto. Abra{" "}
            <strong>Alterando Projeto → SmartFlow Importer</strong> e informe as pastas de
            Fornecedores e/ou Clientes.
          </div>
        )}
        {!camposDoc && (
          <div className="alert alert-warning" role="alert">
            O projeto não tem a URL da Service Layer configurada — a gravação real no SAP ficará
            indisponível.
          </div>
        )}

        <div className="d-flex align-items-center mb-3" style={{ gap: "0.75rem" }}>
          <Button
            color="success"
            onClick={handleListar}
            disabled={listLoading || pastasConectadas.length === 0}
          >
            {listLoading ? "Listando..." : "Listar arquivos"}
          </Button>
          {notas !== null && (
            <span className="text-muted small">
              {notas.length} nota{notas.length === 1 ? "" : "s"} identificada
              {notas.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: openTab === "notas" })}
              onClick={() => setOpenTab("notas")}
              style={{ cursor: "pointer" }}
            >
              Notas identificadas{notas !== null ? ` (${notas.length})` : ""}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: openTab === "receita" })}
              onClick={() => receitaRows !== null && setOpenTab("receita")}
              disabled={receitaRows === null && !receitaLoading}
              style={{ cursor: receitaRows === null ? "not-allowed" : "pointer" }}
            >
              Validação Receita
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: openTab === "importacao" })}
              onClick={() => importRows !== null && setOpenTab("importacao")}
              disabled={importRows === null}
              style={{ cursor: importRows === null ? "not-allowed" : "pointer" }}
            >
              Validação SAP{importRows !== null ? ` (${importRows.length})` : ""}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={openTab} className="pt-4">
          {/*====== Aba: Notas identificadas ======*/}
          <TabPane tabId="notas">
            <div className="d-flex align-items-center mb-3" style={{ gap: "0.75rem" }}>
              <Button color="info" size="sm" onClick={handleListar} disabled={listLoading}>
                {listLoading ? "Listando..." : "Re-listar notas"}
              </Button>
              <Button
                color="success"
                size="sm"
                onClick={handleValidarReceita}
                disabled={receitaLoading || selectedIds.size === 0}
              >
                {receitaLoading ? "Validando..." : "Validar Receita"}
              </Button>
              {notas !== null && notas.length > 0 && (
                <span className="text-muted small">
                  {selectedIds.size} nota{selectedIds.size === 1 ? "" : "s"} selecionada
                  {selectedIds.size === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {listError && <div className="alert alert-danger">{listError}</div>}
            {listLoading && <p className="text-muted small">Lendo os arquivos da pasta...</p>}

            {!listLoading && notas !== null && !listError && (
              <>
                <p className="text-muted small">
                  {notas.length === 0
                    ? "Nenhuma nota fiscal identificada nas pastas."
                    : `${notas.length} nota(s) fiscal(is) identificada(s).`}
                  {naoIdentificados.length > 0 &&
                    ` ${naoIdentificados.length} arquivo(s) XML não reconhecido(s) como NF-e.`}
                  {filtroNotas.trim() && ` Mostrando ${notasFiltradas.length} de ${notas.length}.`}
                </p>

                {notas.length > 0 && (
                  <Input
                    type="text"
                    bsSize="sm"
                    className="mb-3"
                    style={{ maxWidth: 420 }}
                    placeholder="Buscar (NF, CNPJ/CPF, ID estrangeiro, razão social, arquivo...)"
                    value={filtroNotas}
                    onChange={(e) => setFiltroNotas(e.target.value)}
                  />
                )}

                {(notas.length > 0 || naoIdentificados.length > 0) && (
                  <div className="table-responsive">
                    <Table className="align-items-center table-flush" responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={todasSelecionadas}
                              onChange={toggleSelecionarTodas}
                              disabled={!notas || notas.length === 0}
                            />{" "}
                            Válido
                          </th>
                          <th>Tipo</th>
                          <th>Nº NF</th>
                          <th>CNPJ/CPF</th>
                          <th>Razão social</th>
                          <th>Nome fantasia</th>
                          <th>Emissão</th>
                          <th>Valor</th>
                          <th>PN já importado</th>
                          <th>Arquivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notasFiltradas.map((nota) => {
                          const cnpjDigits = String(nota.cnpjCpf || "").replace(/\D/g, "")
                          const cardCodeExistente = parceirosJaImportados[cnpjDigits]
                          return (
                            <tr key={nota.fileId}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(nota.fileId)}
                                  onChange={() => toggleSelecionada(nota.fileId)}
                                />
                              </td>
                              <td>
                                <TipoBadge tipo={nota.tipo} />
                              </td>
                              <td>{nota.numeroNF || "—"}</td>
                              <td>{formatCnpjCpf(nota.cnpjCpf, nota.idEstrangeiro)}</td>
                              <td>{nota.razaoSocial || "—"}</td>
                              <td>{nota.nomeFantasia || "—"}</td>
                              <td>{formatDataEmissao(nota.dataEmissao)}</td>
                              <td>{formatValor(nota.valorTotal)}</td>
                              <td>
                                {cardCodeExistente ? (
                                  <Badge color="warning" title="Provável XML repetido">
                                    Sim — {cardCodeExistente}
                                  </Badge>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-muted small">{nota.fileName}</td>
                            </tr>
                          )
                        })}
                        {naoIdentificados.map((item) => (
                          <tr key={item.fileId} className="table-light">
                            <td>—</td>
                            <td>—</td>
                            <td colSpan={5} className="text-danger">
                              {item.motivo}
                            </td>
                            <td>—</td>
                            <td>—</td>
                            <td className="text-muted small">{item.fileName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </TabPane>

          {/*====== Aba: Validação Receita ======*/}
          <TabPane tabId="receita">
            {receitaLoading && (
              <p className="text-muted small">
                Consultando a Receita Federal (BrasilAPI), CNPJ a CNPJ...
              </p>
            )}

            {!receitaLoading && receitaRows !== null && (
              <>
                <div className="d-flex align-items-center mb-3" style={{ gap: "0.75rem" }}>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handlePrepararImportacao}
                    disabled={selectedReceitaIds.size === 0}
                  >
                    Preparar dados para a importação
                  </Button>
                  {receitaSelecionaveis.length > 0 && (
                    <span className="text-muted small">
                      {selectedReceitaIds.size} selecionada(s)
                    </span>
                  )}
                </div>

                {/*====== resumo do lote (mesmo texto do LiveModa) ======*/}
                {(() => {
                  const naoAplCpf = receitaRows.filter(
                    (r) => r.receitaNaoAplicavel && String(r.motivoNaoAplicavel || "").startsWith("CPF")
                  ).length
                  const naoAplEstr = receitaRows.filter(
                    (r) => r.receitaNaoAplicavel && !String(r.motivoNaoAplicavel || "").startsWith("CPF")
                  ).length
                  const comErro = receitaRows.filter((r) => r.receitaErro).length
                  const analisadas = receitaRows.filter((r) => !r.receitaErro && !r.receitaNaoAplicavel)
                  const conferem = analisadas.filter((r) => compararComReceita(r, r.receita).ok).length
                  const divergentes = analisadas.length - conferem
                  return (
                    <p className="text-muted small">
                      {receitaRows.length} nota{receitaRows.length === 1 ? "" : "s"} analisada
                      {receitaRows.length === 1 ? "" : "s"} na Receita Federal.
                      {analisadas.length > 0 && ` ${conferem} confere${conferem === 1 ? "" : "m"}.`}
                      {divergentes > 0 &&
                        ` ${divergentes} com divergência${divergentes === 1 ? "" : "s"}.`}
                      {naoAplCpf > 0 && ` ${naoAplCpf} não aplicáve${naoAplCpf === 1 ? "l" : "is"} (CPF).`}
                      {naoAplEstr > 0 &&
                        ` ${naoAplEstr} não aplicáve${naoAplEstr === 1 ? "l" : "is"} (parceiro estrangeiro).`}
                      {comErro > 0 && ` ${comErro} com erro de consulta.`}
                    </p>
                  )
                })()}

                <div className="table-responsive">
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={todasReceitaSelecionadas}
                            onChange={toggleSelecionarTodasReceita}
                            disabled={receitaSelecionaveis.length === 0}
                          />{" "}
                          Seleção
                        </th>
                        <th>Tipo</th>
                        <th>Situação</th>
                        <th>Nº NF</th>
                        <th>CNPJ/CPF</th>
                        <th>Razão social (XML)</th>
                        <th>Razão social (Receita)</th>
                        <th>Nome fantasia (Receita)</th>
                        <th>Situação cadastral</th>
                        <th>Endereço (Receita)</th>
                        <th>CNAE principal</th>
                        <th>Sócios (QSA)</th>
                        <th>Detalhe da inconsistência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receitaRows.map((row) => {
                        // CPF/estrangeiro (N/A) conta como "ok" pra destaque/seleção — só erro de
                        // verdade (receitaErro) desabilita a linha. Mesmo critério do LiveModa.
                        const cmp = row.receitaErro
                          ? null
                          : row.receitaNaoAplicavel
                          ? { ok: true, razaoBate: true, situacaoAtiva: true, detalhe: "" }
                          : compararComReceita(row, row.receita)
                        const semDadosDaReceita = row.receitaErro || row.receitaNaoAplicavel
                        const podeSelecionar = !row.receitaErro
                        return (
                          <tr key={row.fileId} className={cmp && !cmp.ok ? "table-danger" : undefined}>
                            <td>
                              {row.receitaErro ? (
                                "—"
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={selectedReceitaIds.has(row.fileId)}
                                  onChange={() => toggleSelecionadaReceita(row.fileId)}
                                  disabled={!podeSelecionar}
                                />
                              )}
                            </td>
                            <td>
                              <TipoBadge tipo={row.tipo} />
                            </td>
                            <td>
                              {row.receitaErro ? (
                                <Badge color="secondary">Erro</Badge>
                              ) : row.receitaNaoAplicavel ? (
                                <Badge color="secondary">N/A</Badge>
                              ) : cmp.ok ? (
                                <Badge color="success">Confere</Badge>
                              ) : (
                                <Badge color="danger">Divergência</Badge>
                              )}
                            </td>
                            <td>{row.numeroNF || "—"}</td>
                            <td>{formatCnpjCpf(row.cnpjCpf, row.idEstrangeiro)}</td>
                            <td className={cmp && !cmp.razaoBate ? "text-danger font-weight-bold" : undefined}>
                              {row.razaoSocial || "—"}
                            </td>
                            {semDadosDaReceita ? (
                              <td
                                colSpan={7}
                                className={row.receitaErro ? "small text-warning" : "small text-muted"}
                              >
                                {row.receitaErro ||
                                  row.motivoNaoAplicavel ||
                                  "CPF — validação na Receita não se aplica"}
                              </td>
                            ) : (
                              <>
                                <td
                                  className={
                                    !cmp.razaoBate ? "text-danger font-weight-bold" : undefined
                                  }
                                >
                                  {row.receita?.razao_social || "—"}
                                </td>
                                <td>{row.receita?.nome_fantasia || "—"}</td>
                                <td
                                  className={
                                    !cmp.situacaoAtiva ? "text-danger font-weight-bold" : undefined
                                  }
                                >
                                  {row.receita?.descricao_situacao_cadastral || "—"}
                                </td>
                                <td className="small">{formatEndereco(row.receita)}</td>
                                <td className="small">{row.receita?.cnae_fiscal_descricao || "—"}</td>
                                <td className="small">
                                  {row.receita?.qsa?.length
                                    ? row.receita.qsa.map((s) => s.nome_socio).join(", ")
                                    : "—"}
                                </td>
                                <td className="small text-danger">
                                  {cmp.ok ? "—" : cmp.detalhe}
                                </td>
                              </>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </TabPane>

          {/*====== Aba: Validação SAP ======*/}
          <TabPane tabId="importacao">
            {importRows !== null && (
              <>
                <div className="d-flex align-items-center mb-3" style={{ gap: "0.75rem" }}>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleGravarStaging}
                    disabled={gravandoStaging || selectedImportIds.size === 0}
                  >
                    {gravandoStaging
                      ? progressoGravacao
                        ? `Gravando ${progressoGravacao.atual}/${progressoGravacao.total}...`
                        : "Gravando..."
                      : "Gravar SAP"}
                  </Button>
                  <Button color="secondary" size="sm" onClick={handleExportarExcel}>
                    Gerar Excel
                  </Button>
                  <span className="text-muted small">
                    {selectedImportIds.size} parceiro{selectedImportIds.size === 1 ? "" : "s"}{" "}
                    selecionado{selectedImportIds.size === 1 ? "" : "s"}
                  </span>
                </div>

                {progressoGravacao && (
                  <div className="mb-3">
                    <div className="progress" style={{ height: 6 }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width: `${Math.round(
                            (progressoGravacao.atual / Math.max(progressoGravacao.total, 1)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                    <p className="text-muted small mt-1 mb-0">
                      {progressoGravacao.etapa} ({progressoGravacao.atual} de{" "}
                      {progressoGravacao.total}) — cada parceiro faz várias chamadas à Service
                      Layer, pode levar alguns segundos por linha.
                    </p>
                  </div>
                )}

                <p
                  className="small text-warning bg-transparent border border-warning rounded p-2 mb-3"
                >
                  "Gravar SAP" grava de verdade no SAP Business One (Service Layer do projeto),
                  mantendo o banco interno do SmartFlow como espelho de cada tentativa.
                </p>
                <p className="text-muted small">
                  {importRows.length} parceiro{importRows.length === 1 ? "" : "s"} preparado
                  {importRows.length === 1 ? "" : "s"} (documento único — notas repetidas do mesmo
                  parceiro já foram descartadas).
                </p>

                <div className="table-responsive">
                  <Table className="align-items-center table-flush" responsive size="sm">
                    <thead className="thead-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={todosImportSelecionados}
                            onChange={toggleSelecionarTodosImport}
                            disabled={importSelecionaveis.length === 0}
                          />
                        </th>
                        <th>Tipo</th>
                        <th>Nota de origem</th>
                        <th>CardCode</th>
                        <th>CardType</th>
                        <th>CardName (Razão social)</th>
                        <th>CardForeignName (Nome fantasia)</th>
                        <th>FederalTaxID (CNPJ)</th>
                        <th>Inscrição Estadual</th>
                        <th>E-mail</th>
                        <th>Telefone</th>
                        <th>Tipo de Logradouro</th>
                        <th>Logradouro</th>
                        <th>Número</th>
                        <th>Complemento</th>
                        <th>Bairro</th>
                        <th>Cidade</th>
                        <th>UF</th>
                        <th>CEP</th>
                        <th>Valid</th>
                        <th>TaxID Estrangeiro</th>
                        <th>Status SAP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.map((row) => {
                        const val = validacoesImport.get(row.fileId) || { ok: true, erros: [] }
                        const res = resultadosGravacao[row.fileId]
                        const cell = (field, width = 110) => (
                          <Input
                            bsSize="sm"
                            style={{ minWidth: width }}
                            value={row[field] || ""}
                            onChange={(e) => updateImportField(row.fileId, field, e.target.value)}
                          />
                        )
                        return (
                          <tr
                            key={row.fileId}
                            className={
                              res?.status === "sucesso"
                                ? "table-success"
                                : res?.status === "erro"
                                ? "table-danger"
                                : row.jaGravadoNoSap
                                ? "table-warning"
                                : undefined
                            }
                            title={
                              res?.status === "erro"
                                ? res.mensagem
                                : row.jaGravadoNoSap
                                ? "Já existe no SAP — será atualizado (CardCode reaproveitado)"
                                : undefined
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedImportIds.has(row.fileId)}
                                onChange={() => toggleImportFlag(row.fileId)}
                                disabled={!val.ok}
                              />
                            </td>
                            <td>
                              <TipoBadge tipo={row.tipo} />
                            </td>
                            <td className="small text-muted" style={{ minWidth: 150 }}>
                              {(row.numeroNF || "—") + (row.fileName ? ` — ${row.fileName}` : "")}
                            </td>
                            <td>{cell("CardCode", 120)}</td>
                            <td className="small">
                              {row.CardType === "cCustomer" ? "Cliente" : "Fornecedor"}
                            </td>
                            <td>{cell("CardName", 200)}</td>
                            <td>{cell("CardForeignName", 180)}</td>
                            <td>{cell("FederalTaxID", 140)}</td>
                            <td>{cell("InscricaoEstadual", 120)}</td>
                            <td>{cell("Email", 160)}</td>
                            <td>{cell("Telefone", 120)}</td>
                            <td>{cell("TipoLogradouro", 110)}</td>
                            <td>{cell("Street", 160)}</td>
                            <td>{cell("StreetNo", 70)}</td>
                            <td>{cell("Complemento", 110)}</td>
                            <td>{cell("Block", 120)}</td>
                            <td>{cell("City", 130)}</td>
                            <td>{cell("County", 60)}</td>
                            <td>{cell("ZipCode", 90)}</td>
                            <td>
                              <Input
                                type="select"
                                bsSize="sm"
                                value={row.Valid}
                                onChange={(e) => updateImportField(row.fileId, "Valid", e.target.value)}
                              >
                                <option value="tYES">Ativo</option>
                                <option value="tNO">Inativo</option>
                              </Input>
                            </td>
                            <td>{cell("TaxIdEstrangeiro", 130)}</td>
                            <td className="small">
                              {res?.status === "sucesso" ? (
                                <Badge color="success">Gravado</Badge>
                              ) : res?.status === "erro" ? (
                                <Badge color="danger" title={res.mensagem}>
                                  Falhou
                                </Badge>
                              ) : val.ok ? (
                                <Badge color="success">OK</Badge>
                              ) : (
                                <Badge color="danger" title={val.erros.join(" ")}>
                                  {val.erros.length} erro(s)
                                </Badge>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
                <Row>
                  <Col>
                    <p className="text-muted small mt-2">
                      Endereço sempre a partir do XML da nota. CardCode reaproveitado quando o CNPJ
                      já foi gravado no SAP (a gravação vira uma atualização — linha em amarelo).
                      Município resolvido pelo código IBGE contra a tabela de municípios do SAP no
                      momento da gravação. São gravados dois endereços por Parceiro (Principal /
                      Pagar a e Entrega / Ponto de entrega).
                    </p>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  )
}
